import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { secureGet } from "../../config/api";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import SnackbarNotification from "../../components/SnackbarNotification";

import EyeIcon from "../../icons/eye.svg";

const STATUS_LABELS = {
  EN_ATTENTE: "En attente",
  REPONDU: "Répondu",
};

const STATUS_COLORS = {
  EN_ATTENTE: "#FEA26F",
  REPONDU: "#06D6A0",
};

const Propositions = () => {
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    needle: "",
    clientId: null,
    requestedById: null,
    status: "",
  });
  const [clients, setClients] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const history = useHistory();

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const columns = [
    { label: "Devis", key: "quoteNumber" },
    { label: "Client", key: "clientName" },
    { label: "Demandé par", key: "requestedByName" },
    {
      label: "Articles",
      key: "articlesCount",
      render: (count, row) => (
        <div>
          <span>{count ?? 0} Articles</span>
          {row.pendingCount > 0 && (
            <p className="text-[#FFFFFF] text-[12px] font-semibold rounded-[12px] w-fit text-center px-2 py-0.5" style={{backgroundColor: "#FEA26F" }}>
              {row.pendingCount} nécessite{row.pendingCount > 1 ? "nt" : ""} une réponse
            </p>
          )}
        </div>
      ),
    },
    {
      label: "Statut",
      key: "status",
      render: (status) => (
        <span
          className="text-white text-[12px] font-semibold px-2 py-1 rounded-full inline-block w-fit text-center"
          style={{ backgroundColor: STATUS_COLORS[status] || "#A8ADC3" }}
        >
          {STATUS_LABELS[status].toUpperCase() || status}
        </span>
      ),
    },
    {
      key: "actions",
      render: (_, row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={() => handleDetails(row)}>
            <img src={EyeIcon} alt="Détails" className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: clients.map((client) => ({ value: client.id, reference: client.code, name: client.name })),
    },
    {
      name: "requestedById",
      label: "Demandé par",
      type: "dropdown",
      options: requesters.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName}` })),
    },
    {
      name: "status",
      label: "Statut",
      type: "dropdown",
      options: [
        { value: "EN_ATTENTE", label: STATUS_LABELS.EN_ATTENTE },
        { value: "REPONDU", label: STATUS_LABELS.REPONDU },
      ],
    },
  ];

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const clientsRes = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientsRes.data || []);
        const requestersRes = await secureGet("/users/ROLE_SERVICE_CONSULTANT", showSnackbar, { signal });
        setRequesters(requestersRes.data || []);
      } catch (error) {
        if (error.name !== "AbortError") console.error("Error fetching dropdown options:", error);
      }
    };
    fetchDropdowns();
    return () => abortController.abort();
  }, [abortController, signal]);

  const fetchPaginatedPropositions = useCallback(
    async (page = 0, size = pageSize, activeFilters = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", page.toString());
        params.set("size", size.toString());
        if (activeFilters.needle) params.set("needle", activeFilters.needle);
        if (activeFilters.clientId) params.set("clientId", activeFilters.clientId.toString());
        if (activeFilters.requestedById) params.set("requestedById", activeFilters.requestedById.toString());
        if (activeFilters.status) params.set("status", activeFilters.status);

        const response = await secureGet(`/quotes/propositions?${params.toString()}`, showSnackbar, { signal });
        const { content, totalPages, totalElements } = response.data;
        setCurrentPageData(content || []);
        setTotalPages(totalPages || 1);
        setTotalElements(totalElements || 0);
      } catch (error) {
        if (error.name !== "AbortError") console.error("Error fetching propositions:", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize, signal]
  );

  useEffect(() => {
    fetchPaginatedPropositions(0, pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedPropositions(page, pageSize, filters);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedPropositions(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters, [filterName]: filterValue };
    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedPropositions(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = { needle: "", clientId: null, requestedById: null, status: "" };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedPropositions(0, pageSize, defaultFilters);
  };

  const handleDetails = (row) => {
    history.push({
      pathname: `/propositions/details/${row.quoteId}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - Propositions</title>
      </Helmet>
      <div className="flex items-center mb-[15px]">
        <h1 className="text-[40px] font-semibold">Propositions</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading} minHeight={200}>
        <Table
          columns={columns}
          data={currentPageData}
          emptyLabel="Aucune proposition trouvée"
          onRowClick={handleDetails}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Propositions;
